import xlsx from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { resolveApiUrl, resolveBanco, resolveBranch } from "./api";

const excelType =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";

const columns = [
  { key: "uf", label: "UF", width: 8 },
  { key: "codigoIbge", label: "Código IBGE", width: 14 },
  { key: "municipio", label: "Município", width: 26 },
  { key: "codigoQuesito", label: "Quesito", width: 10 },
  { key: "temaPergunta", label: "Tema / Quesito", width: 64 },
  { key: "decisaoFinal", label: "Decisão Final", width: 16 },
  { key: "evidencia", label: "Evidência", width: 22 },
  { key: "timeline", label: "Timeline", width: 22 },
  { key: "comentarios", label: "Comentários", width: 22 },
  { key: "comentarioPrincipal", label: "Comentário Principal", width: 58 },
  { key: "atualizadoEm", label: "Atualizado Em", width: 22 },
];

const pdfColumnStyles = {
  0: { cellWidth: 24, halign: "center" },
  1: { cellWidth: 50 },
  2: { cellWidth: 82 },
  3: { cellWidth: 40, halign: "center" },
  4: { cellWidth: 150 },
  5: { cellWidth: 48, halign: "center", fontStyle: "bold" },
  6: { cellWidth: 58, halign: "center" },
  7: { cellWidth: 56, halign: "center" },
  8: { cellWidth: 62, halign: "center" },
  9: { cellWidth: 130 },
  10: { cellWidth: 54 },
};

function buildFileName(prefix) {
  return `${prefix}_${new Date().getTime()}`;
}

function repairMojibake(value) {
  const text = String(value ?? "");
  if (!/[ÃÂ�]/.test(text)) return text;

  try {
    const bytes = Uint8Array.from([...text].map((character) => character.charCodeAt(0) & 0xff));
    const decoded = new TextDecoder("utf-8", { fatal: false }).decode(bytes);
    return decoded.includes("�") ? text : decoded;
  } catch {
    return text;
  }
}

function cleanText(value) {
  if (value === null || value === undefined) return "";

  return repairMojibake(value)
    .normalize("NFC")
    .replace(/[\u0000-\u001f\u007f]+/g, " ")
    .replace(/\b(?:[A-Za-zÀ-ÿ]\s+){3,}[A-Za-zÀ-ÿ]\b/g, (match) =>
      match.replace(/\s+/g, "")
    )
    .replace(/\s+/g, " ")
    .trim();
}

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? cleanText(value) : date.toLocaleString("pt-BR");
}

function toAbsoluteUrl(url) {
  if (!url) return "";
  const isInternalApi = /^\/api\//i.test(url) || /\/api\/IaColetaEvidencias\//i.test(url);
  let absoluteUrl = url;

  if (!/^https?:\/\//i.test(url)) {
    if (isInternalApi) {
      absoluteUrl = resolveApiUrl(url);
    } else {
      const configuredOrigin = process.env.VUE_APP_IA_EVIDENCIAS_PUBLIC_ORIGIN;
      const origin = configuredOrigin || window.location.origin;
      absoluteUrl = `${origin.replace(/\/$/, "")}/${String(url).replace(/^\//, "")}`;
    }
  }

  if (!isInternalApi) {
    return absoluteUrl;
  }

  absoluteUrl = appendQueryIfMissing(absoluteUrl, "banco", resolveBanco());
  absoluteUrl = appendQueryIfMissing(absoluteUrl, "branch", resolveBranch());

  return absoluteUrl;
}

function appendQueryIfMissing(url, key, value) {
  if (!value || new RegExp(`[?&]${key}=`, "i").test(url)) return url;

  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}${key}=${encodeURIComponent(value)}`;
}

function getLink(row, field) {
  return toAbsoluteUrl(row?.[field]?.url || "");
}

function getValue(row, key) {
  if (key === "temaPergunta") {
    const tema = cleanText(row?.tema);
    const pergunta = cleanText(row?.pergunta);

    if (tema && pergunta) return `${tema} - ${pergunta}`;
    return tema || pergunta;
  }

  if (key === "evidencia") {
    return getLink(row, "evidencia") ? "Baixar evidência" : "";
  }

  if (key === "timeline") {
    return getLink(row, "timeline") ? "Baixar timeline" : "";
  }

  if (key === "comentarios") {
    return getLink(row, "comentarios") ? "Baixar comentários" : "";
  }

  if (key === "atualizadoEm") {
    return formatDate(row.atualizadoEm);
  }

  return cleanText(row?.[key]);
}

function getPdfValue(row, key) {
  const value = getValue(row, key);

  if (key !== "comentarioPrincipal") return value;
  if (value.length <= 760) return value;

  return `${value.slice(0, 757).trim()}...`;
}

function decisionColor(value) {
  const normalized = cleanText(value).toLowerCase();
  if (normalized === "sim") return [39, 122, 74];
  if (normalized === "não" || normalized === "nao") return [181, 63, 52];
  if (normalized === "revisão" || normalized === "revisao") return [185, 112, 18];
  if (normalized === "timeout") return [204, 92, 36];
  if (normalized === "erro") return [170, 40, 40];
  return [82, 82, 82];
}

function setHyperlinkCell(worksheet, cellAddress, label, url, tooltip) {
  worksheet[cellAddress] = {
    t: "s",
    v: label,
    l: { Target: url, Tooltip: tooltip || label },
  };
}

export function exportResultadosXlsx(rows) {
  const aoa = [columns.map((column) => column.label)];

  (rows || []).forEach((row) => {
    aoa.push(columns.map((column) => getValue(row, column.key)));
  });

  const worksheet = xlsx.utils.aoa_to_sheet(aoa);
  worksheet["!cols"] = columns.map((column) => ({ wch: column.width }));
  worksheet["!autofilter"] = { ref: worksheet["!ref"] };
  worksheet["!freeze"] = { xSplit: 0, ySplit: 1 };

  (rows || []).forEach((row, index) => {
    const sheetRow = index + 2;
    const evidenciaUrl = getLink(row, "evidencia");
    const timelineUrl = getLink(row, "timeline");
    const comentariosUrl = getLink(row, "comentarios");

    if (evidenciaUrl) {
      setHyperlinkCell(
        worksheet,
        `G${sheetRow}`,
        "Baixar evidência",
        evidenciaUrl,
        "Baixar evidência completa"
      );
    }

    if (timelineUrl) {
      setHyperlinkCell(
        worksheet,
        `H${sheetRow}`,
        "Baixar timeline",
        timelineUrl,
        "Baixar timeline da execução"
      );
    }

    if (comentariosUrl) {
      setHyperlinkCell(
        worksheet,
        `I${sheetRow}`,
        "Baixar comentários",
        comentariosUrl,
        "Baixar comentários da execução"
      );
    }
  });

  const workbook = {
    Sheets: { Resultados: worksheet },
    SheetNames: ["Resultados"],
  };
  const buffer = xlsx.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([buffer], { type: excelType });
  saveAs(blob, `${buildFileName("ia_coleta_resultados")}.xlsx`);
}

export function exportResultadosPdf(rows, filtros = {}, resumo = {}) {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "pt",
    format: "a4",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  doc.setFillColor(23, 43, 77);
  doc.rect(0, 0, pageWidth, 58, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(15);
  doc.text("IA Coleta de Evidências - Resultados Consolidados", 32, 34);

  doc.setTextColor(40, 40, 40);
  doc.setFontSize(8);
  const filtrosTexto = [
    filtros.uf ? `UF: ${cleanText(filtros.uf)}` : null,
    filtros.municipio ? `Município: ${cleanText(filtros.municipio)}` : null,
    filtros.codigoQuesito ? `Quesito: ${cleanText(filtros.codigoQuesito)}` : null,
    filtros.iaExecucaoId ? `Execução: ${cleanText(filtros.iaExecucaoId)}` : null,
    filtros.decisaoFinal ? `Decisão: ${cleanText(filtros.decisaoFinal)}` : null,
  ]
    .filter(Boolean)
    .join(" | ");

  const resumoTexto = `Total: ${resumo.total || 0} | Sim: ${resumo.yes || 0} | Não: ${
    resumo.no || 0
  } | Revisão: ${resumo.review || 0} | Timeout: ${resumo.timeout || 0} | Erro: ${
    resumo.error || 0
  } | Pendente: ${resumo.pending || 0}`;

  doc.text(filtrosTexto || "Filtros: todos os resultados consolidados", 32, 76);
  doc.text(resumoTexto, 32, 90);

  const linkRows = rows || [];
  const body = linkRows.map((row) =>
    columns.map((column) => getPdfValue(row, column.key))
  );

  doc.autoTable({
    startY: 104,
    head: [columns.map((column) => column.label)],
    body,
    theme: "grid",
    tableWidth: "wrap",
    rowPageBreak: "auto",
    showHead: "everyPage",
    styles: {
      fontSize: 6.1,
      cellPadding: { top: 4, right: 3, bottom: 4, left: 3 },
      overflow: "linebreak",
      valign: "top",
      lineColor: [221, 226, 232],
      lineWidth: 0.35,
      minCellHeight: 18,
    },
    bodyStyles: {
      textColor: [46, 55, 68],
    },
    headStyles: {
      fillColor: [230, 126, 34],
      textColor: 255,
      fontStyle: "bold",
      halign: "center",
      valign: "middle",
    },
    alternateRowStyles: {
      fillColor: [249, 251, 253],
    },
    columnStyles: pdfColumnStyles,
    margin: { left: 18, right: 18 },
    didParseCell(data) {
      if (data.section === "body" && data.column.index === 5) {
        const color = decisionColor(data.cell.raw);
        data.cell.styles.textColor = color;
      }

      if (data.section === "body" && (data.column.index === 4 || data.column.index === 9)) {
        const maxWidth = pdfColumnStyles[data.column.index].cellWidth - 8;
        data.cell.text = doc.splitTextToSize(cleanText(data.cell.raw), maxWidth);
      }

      if (data.section === "body" && (data.column.index === 6 || data.column.index === 7 || data.column.index === 8)) {
        data.cell.text = [""];
      }
    },
    didDrawCell(data) {
      if (data.section !== "body" || (data.column.index !== 6 && data.column.index !== 7 && data.column.index !== 8)) return;

      const row = linkRows[data.row.index];
      const isEvidence = data.column.index === 6;
      const isTimeline = data.column.index === 7;
      const url = isEvidence
        ? getLink(row, "evidencia")
        : isTimeline
          ? getLink(row, "timeline")
          : getLink(row, "comentarios");
      const label = isEvidence ? "Baixar evidência" : isTimeline ? "Baixar timeline" : "Baixar comentários";

      if (!url) return;

      doc.setTextColor(25, 92, 170);
      doc.setFontSize(6.1);
      doc.textWithLink(label, data.cell.x + 4, data.cell.y + 12, { url });
    },
    didDrawPage() {
      const pageNumber = doc.internal.getNumberOfPages();
      doc.setFontSize(7);
      doc.setTextColor(120, 120, 120);
      doc.text(`Página ${pageNumber}`, pageWidth - 58, doc.internal.pageSize.getHeight() - 16);
    },
  });

  doc.save(`${buildFileName("ia_coleta_resultados")}.pdf`);
}
